// ----------------------------------------------------------------------------
// MIDI to MusicXML Converter
// ----------------------------------------------------------------------------
// Converts @tonejs/midi Midi instances to MusicXML format.
//
// Based on the proven bar-layout engine from SeedSong
// (procedural-music-generator-web by chicomcastro), which quantizes note
// timings to a 16th-note grid, fills rests, groups chords, assigns beams,
// and spells notes according to the key signature. The original
// midi-to-musicxml converter produced garbage because it emitted raw tick
// durations without quantization or rest/chord handling.
// ----------------------------------------------------------------------------

// ---- Pitch spelling (sharp-wise for sharp keys, flat-wise for flat keys) ----
const SHARP_STEP_MAP = ['C', 'C', 'D', 'D', 'E', 'F', 'F', 'G', 'G', 'A', 'A', 'B'];
const SHARP_ALTER_MAP = [0, 1, 0, 1, 0, 0, 1, 0, 1, 0, 1, 0];
const FLAT_STEP_MAP  = ['C', 'D', 'D', 'E', 'E', 'F', 'G', 'G', 'A', 'A', 'B', 'B'];
const FLAT_ALTER_MAP  = [0, -1, 0, -1, 0, 0, -1, 0, -1, 0, -1, 0];

const DIVISIONS = 4; // divisions per quarter note

const DURATION_TYPE_MAP = {
  4:    { type: 'whole',    divisions: 16, dot: false },
  3:    { type: 'half',     divisions: 12, dot: true  },
  2:    { type: 'half',     divisions: 8,  dot: false },
  1.5:  { type: 'quarter',  divisions: 6,  dot: true  },
  1:    { type: 'quarter',  divisions: 4,  dot: false },
  0.75: { type: 'eighth',   divisions: 3,  dot: true  },
  0.5:  { type: 'eighth',   divisions: 2,  dot: false },
  0.25: { type: '16th',     divisions: 1,  dot: false },
};

const STANDARD_DURATIONS = [4, 3, 2, 1.5, 1, 0.75, 0.5, 0.25];

// ---- Krumhansl-Schmuckler key profiles for key detection -------------------
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

// ---- Quantization ----------------------------------------------------------
function quantizeDuration(beats) {
  if (beats <= 0) return 0.25;
  let best = 0.25;
  let bestDiff = Math.abs(beats - 0.25);
  for (const d of STANDARD_DURATIONS) {
    const diff = Math.abs(beats - d);
    if (diff < bestDiff) { bestDiff = diff; best = d; }
  }
  return best;
}

function snapToGrid(beat, grid) {
  return Math.round(beat / grid) * grid;
}

// ---- Pitch helpers ---------------------------------------------------------
function midiToPitch(midi, useFlats) {
  const pc = ((midi % 12) + 12) % 12;
  const octave = Math.floor(midi / 12) - 1;
  const step  = useFlats ? FLAT_STEP_MAP[pc]  : SHARP_STEP_MAP[pc];
  const alter = useFlats ? FLAT_ALTER_MAP[pc] : SHARP_ALTER_MAP[pc];
  return { step, alter, octave };
}

function escapeXml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function buildPitchXml(midi, useFlats) {
  const { step, alter, octave } = midiToPitch(midi, useFlats);
  let xml = `          <pitch>\n            <step>${step}</step>\n`;
  if (alter !== 0) xml += `            <alter>${alter}</alter>\n`;
  xml += `            <octave>${octave}</octave>\n          </pitch>`;
  return xml;
}

// ---- Note XML builder ------------------------------------------------------
// `beams` is null, a legacy string, or array of { number, type }.
function buildNoteXml(midi, durationBeats, isChord, isRest, isDrum, beams, useFlats) {
  const qDur = quantizeDuration(durationBeats);
  const info = DURATION_TYPE_MAP[qDur];
  if (!info) return '';

  let xml = '        <note>\n';
  if (isChord) xml += '          <chord/>\n';

  if (isRest) {
    xml += '          <rest/>\n';
  } else if (isDrum) {
    const { step, octave } = midiToPitch(midi, false);
    xml += `          <unpitched>\n            <display-step>${step}</display-step>\n            <display-octave>${octave}</display-octave>\n          </unpitched>\n`;
  } else {
    xml += buildPitchXml(midi, useFlats) + '\n';
  }

  xml += `          <duration>${info.divisions}</duration>\n`;
  xml += '          <voice>1</voice>\n';
  xml += `          <type>${info.type}</type>\n`;
  if (info.dot) xml += '          <dot/>\n';

  const beamList = typeof beams === 'string' ? [{ number: 1, type: beams }] : beams;
  if (Array.isArray(beamList)) {
    for (const b of beamList) {
      xml += `          <beam number="${b.number}">${b.type}</beam>\n`;
    }
  }
  xml += '        </note>\n';
  return xml;
}

// ---- Measure attributes (clef, key, time) ----------------------------------
function buildMeasureAttributes(beatsPerBar, beatType, isDrum, clefSign, fifths) {
  let xml = '        <attributes>\n';
  xml += `          <divisions>${DIVISIONS}</divisions>\n`;
  xml += `          <key>\n            <fifths>${fifths}</fifths>\n          </key>\n`;
  xml += `          <time>\n            <beats>${beatsPerBar}</beats>\n            <beat-type>${beatType}</beat-type>\n          </time>\n`;
  if (isDrum) {
    xml += '          <clef>\n            <sign>percussion</sign>\n          </clef>\n';
  } else if (clefSign === 'F') {
    xml += '          <clef>\n            <sign>F</sign>\n            <line>4</line>\n          </clef>\n';
  } else if (clefSign === 'C') {
    xml += '          <clef>\n            <sign>C</sign>\n            <line>3</line>\n          </clef>\n';
  } else {
    xml += '          <clef>\n            <sign>G</sign>\n            <line>2</line>\n          </clef>\n';
  }
  xml += '        </attributes>\n';
  return xml;
}

function buildTempoDirection(bpm) {
  return `        <direction placement="above">\n          <direction-type>\n            <metronome>\n              <beat-unit>quarter</beat-unit>\n              <per-minute>${bpm}</per-minute>\n            </metronome>\n          </direction-type>\n          <sound tempo="${bpm}"/>\n        </direction>\n`;
}

// ---- Rest gap filling ------------------------------------------------------
function fillRestGap(gapBeats) {
  let xml = '';
  let remaining = gapBeats;
  while (remaining > 0.001) {
    const qDur = quantizeDuration(remaining);
    const actualDur = Math.min(qDur, remaining);
    const finalDur = quantizeDuration(actualDur);
    const info = DURATION_TYPE_MAP[finalDur];
    if (!info || finalDur > remaining + 0.001) {
      let found = false;
      for (const d of STANDARD_DURATIONS) {
        if (d <= remaining + 0.001) {
          const dInfo = DURATION_TYPE_MAP[d];
          if (dInfo) {
            xml += buildNoteXml(0, d, false, true, false, null, false);
            remaining -= d;
            found = true;
            break;
          }
        }
      }
      if (!found) break;
    } else {
      xml += buildNoteXml(0, finalDur, false, true, false, null, false);
      remaining -= finalDur;
    }
  }
  return xml;
}

// ---- Clef detection --------------------------------------------------------
function detectClef(events) {
  if (events.length === 0) return 'G';
  const avgMidi = events.reduce((s, e) => s + e.midi, 0) / events.length;
  return avgMidi < 55 ? 'F' : 'G';
}

// ---- Key detection (Krumhansl-Schmuckler) ----------------------------------
// Returns { fifths: number } — the MusicXML key signature value.
function detectKey(allNotes) {
  if (!allNotes || allNotes.length === 0) return { fifths: 0 };

  // Build pitch-class histogram weighted by note duration
  const pcHist = new Array(12).fill(0);
  for (const n of allNotes) {
    const pc = ((n.midi % 12) + 12) % 12;
    const weight = Math.max(0.25, n.durationBeats);
    pcHist[pc] += weight;
  }

  // Correlate with all 24 major/minor profiles
  let bestScore = -Infinity;
  let bestKey = 0; // C major
  let bestIsMinor = false;

  for (let i = 0; i < 12; i++) {
    // Major key with tonic = i
    let majorScore = 0;
    let minorScore = 0;
    for (let j = 0; j < 12; j++) {
      const idx = (i + j) % 12;
      majorScore += pcHist[idx] * MAJOR_PROFILE[j];
      minorScore += pcHist[idx] * MINOR_PROFILE[j];
    }
    if (majorScore > bestScore) { bestScore = majorScore; bestKey = i; bestIsMinor = false; }
    if (minorScore > bestScore) { bestScore = minorScore; bestKey = i; bestIsMinor = true; }
  }

  // Convert tonic pitch class + mode to fifths
  // Circle of fifths: C=0, G=1, D=2, A=3, E=4, B=5, F#=-6, ...
  // For minor keys, use the relative major (minor tonic + 3 semitones = relative major)
  const majorPc = bestIsMinor ? (((bestKey + 3) % 12) + 12) % 12 : (((bestKey % 12) + 12) % 12);
  // 7 is its own inverse mod 12 — recover circle-of-fifths position
  let f = (majorPc * 7) % 12;
  if (f > 6) f -= 12;
  return { fifths: f };
}

// ---- Bar layout engine (ported from SeedSong) ------------------------------
function buildPartMeasures(events, beatsPerBar, beatType, totalBars, isDrum, bpm, isFirstPart, clefSign, fifths) {
  const GRID = 0.25;
  let xml = '';

  for (let bar = 0; bar < totalBars; bar++) {
    const barStart = bar * beatsPerBar;
    const barEnd = barStart + beatsPerBar;
    const useFlats = fifths < 0;

    const barEvents = events
      .filter(ev => ev.atBeat >= barStart && ev.atBeat < barEnd)
      .map(ev => {
        const snappedLocal = snapToGrid(ev.atBeat - barStart, GRID);
        const snappedDur = snapToGrid(Math.min(ev.durationBeats, barEnd - ev.atBeat), GRID) || GRID;
        return { ...ev, localBeat: Math.min(snappedLocal, beatsPerBar - GRID), durationBeats: snappedDur };
      })
      .sort((a, b) => a.localBeat - b.localBeat || a.midi - b.midi);

    xml += `      <measure number="${bar + 1}">\n`;

    if (bar === 0) {
      xml += buildMeasureAttributes(beatsPerBar, beatType, isDrum, clefSign, fifths);
      if (isFirstPart) xml += buildTempoDirection(bpm);
    }

    if (barEvents.length === 0) {
      xml += '        <note>\n          <rest measure="yes"/>\n';
      xml += `          <duration>${beatsPerBar * DIVISIONS}</duration>\n`;
      xml += '          <voice>1</voice>\n';
      xml += '        </note>\n';
    } else {
      const beatPositions = [...new Set(barEvents.map(ev => ev.localBeat))].sort((a, b) => a - b);

      const slots = [];
      for (const beatPos of beatPositions) {
        const notesAtBeat = barEvents.filter(ev => Math.abs(ev.localBeat - beatPos) < 0.001);
        const noteDur = quantizeDuration(notesAtBeat[0].durationBeats);
        slots.push({ beatPos, notes: notesAtBeat, dur: noteDur });
      }

      // Pass 1: resolve each slot's position + duration and preceding rest
      const items = [];
      let usedBeats = 0;
      for (const slot of slots) {
        let restBefore = 0;
        if (slot.beatPos > usedBeats + 0.001) {
          restBefore = slot.beatPos - usedBeats;
          usedBeats = slot.beatPos;
        }
        const cappedDur = Math.min(slot.dur, beatsPerBar - usedBeats);
        const finalDur = quantizeDuration(cappedDur);
        items.push({ beatPos: slot.beatPos, finalDur, notes: slot.notes, restBefore });
        usedBeats += finalDur;
      }
      const trailingRest = usedBeats < beatsPerBar - 0.001 ? beatsPerBar - usedBeats : 0;

      // Pass 2: assign multi-level beams
      const beamLevels = (dur) => (dur === 0.25 ? 2 : (dur === 0.5 || dur === 0.75) ? 1 : 0);
      const beamsFor = items.map(() => null);
      if (!isDrum) {
        let g = 0;
        while (g < items.length) {
          if (beamLevels(items[g].finalDur) === 0) { g++; continue; }
          let end = g;
          while (end + 1 < items.length) {
            const cur = items[end];
            const nxt = items[end + 1];
            const contiguous = Math.abs(cur.beatPos + cur.finalDur - nxt.beatPos) < 0.001;
            const sameBeat = Math.floor(cur.beatPos) === Math.floor(nxt.beatPos);
            if (beamLevels(nxt.finalDur) > 0 && contiguous && sameBeat && nxt.restBefore < 0.001) end++;
            else break;
          }
          if (end > g) {
            const group = items.slice(g, end + 1);
            const maxLevel = Math.max(...group.map(it => beamLevels(it.finalDur)));
            for (let gi = 0; gi < group.length; gi++) {
              const beams = [];
              for (let lvl = 1; lvl <= maxLevel; lvl++) {
                if (beamLevels(group[gi].finalDur) < lvl) continue;
                const hasPrev = gi > 0 && beamLevels(group[gi - 1].finalDur) >= lvl;
                const hasNext = gi < group.length - 1 && beamLevels(group[gi + 1].finalDur) >= lvl;
                let type;
                if (hasPrev && hasNext) type = 'continue';
                else if (!hasPrev && hasNext) type = 'begin';
                else if (hasPrev && !hasNext) type = 'end';
                else type = gi === 0 ? 'forward hook' : 'backward hook';
                beams.push({ number: lvl, type });
              }
              beamsFor[g + gi] = beams;
            }
          }
          g = end + 1;
        }
      }

      // Emit interleaved rests + notes
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.restBefore > 0.001) xml += fillRestGap(it.restBefore);
        let isFirst = true;
        for (const ev of it.notes) {
          xml += buildNoteXml(ev.midi, it.finalDur, !isFirst, false, isDrum, isFirst ? beamsFor[i] : null, useFlats);
          isFirst = false;
        }
      }
      if (trailingRest > 0.001) xml += fillRestGap(trailingRest);
    }

    xml += '      </measure>\n';
  }

  return xml;
}

// ---- MIDI → events converter -----------------------------------------------
// Converts a @tonejs/midi track into beat-based events:
//   { atBeat, durationBeats, midi, velocity }
function trackToEvents(track, ticksPerQuarter) {
  if (!track.notes || track.notes.length === 0) return [];
  return track.notes
    .map(n => ({
      atBeat: n.ticks / ticksPerQuarter,
      durationBeats: Math.max(0.25, (n.durationTicks || 0) / ticksPerQuarter),
      midi: n.midi,
      velocity: (n.velocity || 64) / 127,
    }))
    .sort((a, b) => a.atBeat - b.atBeat || a.midi - b.midi);
}

// Detect if a track is a drum track (channel 9 = MIDI channel 10)
function isDrumTrack(track) {
  if (!track.notes || track.notes.length === 0) return false;
  // @tonejs/midi: channel 9 is drums (0-indexed)
  if (track.channel === 9) return true;
  // Fallback: check instrument name
  if (track.instrument && track.instrument.percussion) return true;
  return false;
}

// ---- Main entry point ------------------------------------------------------
// @param {Object} midi - @tonejs/midi Midi instance
// @returns {string} - MusicXML 4.0 partwise document string
function midiToMusicXml(midi, title) {
  const header = midi.header;
  const tracks = midi.tracks || [];

  const ticksPerQuarter = header.ticksPerQuarter || 480;

  // Tempo: use first tempo event, or default 120. Round to avoid FP noise.
  const rawBpm = (header.tempos && header.tempos.length > 0) ? header.tempos[0].bpm : 120;
  const bpm = Math.round(rawBpm * 10) / 10;

  // Time signature: use first time sig event, or default 4/4
  let beatsPerBar = 4;
  let beatType = 4;
  if (header.timeSignatures && header.timeSignatures.length > 0) {
    const ts = header.timeSignatures[0];
    beatsPerBar = ts.timeSignature[0];
    beatType = ts.timeSignature[1];
  }
  // OSMD handles 4/4, 3/4, 2/4, 6/8, etc. For compound meters like 6/8,
  // beatsPerBar is the number of beats and beatType the beat unit.
  // Our bar-layout engine works in "beats" — for 6/8 we use 6 beats of 8th.
  // Normalize: if beatType is 8, keep beatsPerBar as-is (e.g. 6/8 = 6 eighth beats).

  // Collect all tracks that have notes
  const tracksWithNotes = tracks.filter(t => t.notes && t.notes.length > 0);

  if (tracksWithNotes.length === 0) {
    // Return minimal valid MusicXML
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">\n';
    xml += '<score-partwise version="4.0">\n';
    if (title) {
      xml += `  <work><work-title>${escapeXml(title)}</work-title></work>\n`;
      xml += `  <movement-title>${escapeXml(title)}</movement-title>\n`;
    }
    xml += '  <part-list>\n    <score-part id="P1">\n      <part-name>Empty</part-name>\n    </score-part>\n  </part-list>\n';
    xml += '  <part id="P1">\n    <measure number="1">\n';
    xml += `      <attributes>\n        <divisions>${DIVISIONS}</divisions>\n        <time>\n          <beats>${beatsPerBar}</beats>\n          <beat-type>${beatType}</beat-type>\n        </time>\n        <clef>\n          <sign>G</sign>\n          <line>2</line>\n        </clef>\n      </attributes>\n`;
    xml += `      ${buildTempoDirection(bpm)}`;
    xml += '      <note><rest measure="yes"/><duration>16</duration><voice>1</voice></note>\n';
    xml += '    </measure>\n  </part>\n</score-partwise>\n';
    return xml;
  }

  // Convert each track to events
  const trackData = tracksWithNotes.map((track, idx) => {
    const events = trackToEvents(track, ticksPerQuarter);
    const isDrum = isDrumTrack(track);
    const name = track.name || `Track ${idx + 1}`;
    return { events, isDrum, name, track };
  });

  // Detect key signature from all notes combined
  const allNotes = trackData.flatMap(td => td.events);
  const { fifths } = detectKey(allNotes);

  // Calculate total bars from the latest note end across all tracks
  const maxBeat = trackData.reduce((max, td) => {
    const trackMax = td.events.reduce((m, ev) => Math.max(m, ev.atBeat + ev.durationBeats), 0);
    return Math.max(max, trackMax);
  }, 0);
  const totalBars = Math.max(1, Math.ceil(maxBeat / beatsPerBar));

  // Build the MusicXML document
  let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
  xml += '<!DOCTYPE score-partwise PUBLIC "-//Recordare//DTD MusicXML 4.0 Partwise//EN" "http://www.musicxml.org/dtds/partwise.dtd">\n';
  xml += '<score-partwise version="4.0">\n';
  if (title) {
    xml += `  <work><work-title>${escapeXml(title)}</work-title></work>\n`;
    xml += `  <movement-title>${escapeXml(title)}</movement-title>\n`;
  }
  xml += '  <identification>\n    <encoding>\n      <software>AZOPSOFT MIDI to MusicXML</software>\n    </encoding>\n  </identification>\n';

  // Part list
  xml += '  <part-list>\n';
  trackData.forEach((td, i) => {
    const partId = `P${i + 1}`;
    xml += `    <score-part id="${partId}">\n`;
    xml += `      <part-name>${escapeXml(td.name)}</part-name>\n`;
    xml += '    </score-part>\n';
  });
  xml += '  </part-list>\n';

  // Parts
  trackData.forEach((td, i) => {
    const partId = `P${i + 1}`;
    const clefSign = td.isDrum ? 'percussion' : detectClef(td.events);
    xml += `  <part id="${partId}">\n`;
    xml += buildPartMeasures(
      td.events, beatsPerBar, beatType, totalBars,
      td.isDrum, bpm, i === 0, clefSign, fifths
    );
    xml += '  </part>\n';
  });

  xml += '</score-partwise>\n';
  return xml;
}

module.exports = { midiToMusicXml };
